import { css } from '@inter-ikea-kompis/web-component';

const TRANSITION_TIME = '0.2s';

type StyleOptionsType = {
	expanded: boolean;
	horizontal: boolean;
	isAnimationCompleted: boolean;
	contentSize: number;
};

/**
 * A class with style.
 */
export default class CollapsibleStyle {
	/**
	 * Returns styles.
	 *
	 * @param options Options.
	 * @returns Styles.
	 */
	public static getStyle(options: StyleOptionsType): string {
		if (!options.expanded && options.isAnimationCompleted) {
			return this.getFullyClosedStyle(options) + this.getPrintStyle();
		} else if (options.expanded && options.isAnimationCompleted) {
			return this.getFullyOpenStyle() + this.getPrintStyle();
		} else if (!options.expanded) {
			return this.getClosingStyle(options) + this.getPrintStyle();
		}
		return this.getOpeningStyle(options) + this.getPrintStyle();
	}

	/**
	 * @returns Styles.
	 */
	private static getFullyOpenStyle(): string {
		return css`
			.container {
				display: block;
				overflow: visible;
			}
		`;
	}

	/**
	 * Returns styles.
	 *
	 * @param options Options.
	 * @returns Styles.
	 */
	private static getOpeningStyle(options: StyleOptionsType): string {
		return css`
			.container {
				display: block;
				overflow: hidden;
				animation-fill-mode: both;
				animation-name: opening;
				animation-timing-function: ease-in-out;
				animation-duration: ${TRANSITION_TIME};
			}
			@keyframes opening {
				from {
					${this.getChangingRule(options)}: 0;
					opacity: 0;
				}
				to {
					${this.getChangingRule(options)}: ${options.contentSize}px;
					opacity: 1;
				}
			}
		`;
	}

	/**
	 * Returns styles.
	 *
	 * @param options ...
	 * @returns Styles.
	 */
	private static getClosingStyle(options: StyleOptionsType): string {
		return css`
			.container {
				display: block;
				overflow: hidden;
				animation-name: closing;
				animation-fill-mode: both;
				animation-timing-function: ease-in-out;
				animation-duration: ${TRANSITION_TIME};
			}
			@keyframes closing {
				from {
					${this.getChangingRule(options)}: ${options.contentSize}px;
					opacity: 1;
				}
				to {
					${this.getChangingRule(options)}: 0;
					opacity: 0;
				}
			}
		`;
	}

	/**
	 * Returns styles.
	 *
	 * @param options Options.
	 * @returns Styles.
	 */
	private static getChangingRule(options: StyleOptionsType): 'max-width' | 'max-height' {
		if (options.horizontal) {
			return 'max-width';
		}
		return 'max-height';
	}

	/**
	 * Returns styles.
	 *
	 * @param options Options.
	 * @returns Styles.
	 */
	private static getFullyClosedStyle(options: StyleOptionsType): string {
		return css`
			.container {
				display: block;
				${options.horizontal ? 'width: 0;' : 'height: 0;'}
				overflow: hidden;
				${this.getChangingRule(options)}: 0;
				visibility: hidden;
				pointer-events: none;
			}
		`;
	}

	/**
	 * Returns styles.
	 *
	 * @returns Styles.
	 */
	private static getPrintStyle(): string {
		return css`
			@media print {
				.container {
					display: block;
					overflow: visible;
					max-height: unset;
					height: unset;
					visibility: visible;
				}
			}
		`;
	}
}
